import { useEffect, useRef, useState } from 'react';
import { animate } from 'animejs';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { useReducedMotion } from '../../hooks/useReducedMotion';

interface ComplianceGaugeProps {
    percentage: number;
    title: string;
    subtitle?: string;
    size?: number;
}

export default function ComplianceGauge({
    percentage,
    title,
    subtitle,
    size = 160,
}: ComplianceGaugeProps) {
    const [animatedPercentage, setAnimatedPercentage] = useState(0);
    const circleRef = useRef<SVGCircleElement>(null);
    const reducedMotion = useReducedMotion();
    const { ref: containerRef, isInView } = useScrollAnimation<HTMLDivElement>({ threshold: 0.3 });
    const hasAnimated = useRef(false);

    const radius = (size - 16) / 2;
    const circumference = 2 * Math.PI * radius;

    useEffect(() => {
        if (!isInView || hasAnimated.current) return;

        if (reducedMotion) {
            setAnimatedPercentage(percentage);
            hasAnimated.current = true;
            return;
        }

        const percentageObj = { value: 0 };

        animate(percentageObj, {
            value: percentage,
            duration: 2000,
            ease: 'outExpo',
            onUpdate: () => {
                setAnimatedPercentage(Math.round(percentageObj.value));
            },
            onComplete: () => {
                hasAnimated.current = true;
            }
        });

        if (circleRef.current) {
            const targetOffset = circumference - (percentage / 100) * circumference;
            animate(circleRef.current, {
                strokeDashoffset: targetOffset,
                duration: 2000,
                ease: 'outExpo',
            });
        }
    }, [isInView, percentage, circumference, reducedMotion]);

    const getColor = () => {
        if (percentage >= 90) return '#10b981';
        if (percentage >= 70) return '#f59e0b';
        return '#ef4444';
    };

    const getGradientId = () => {
        if (percentage >= 90) return 'safetyGradient';
        if (percentage >= 70) return 'alertGradient';
        return 'dangerGradient';
    };

    const getStatusText = () => {
        if (percentage >= 90) return 'Excellent';
        if (percentage >= 70) return 'Needs Attention';
        return 'Critical';
    };

    return (
        <div
            ref={containerRef}
            className={`card-terminal p-8 flex flex-col items-center gpu-accelerated ${isInView ? 'animate-scaleUp opacity-100' : 'opacity-0'
                }`}
            role="meter"
            aria-label={`${title}: ${percentage}% compliant`}
            aria-valuenow={percentage}
            aria-valuemin={0}
            aria-valuemax={100}
        >
            <div className="relative" style={{ width: size, height: size }}>
                <svg className="transform -rotate-90" width={size} height={size}>
                    <defs>
                        <linearGradient id="safetyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#10b981" />
                            <stop offset="100%" stopColor="#059669" />
                        </linearGradient>
                        <linearGradient id="alertGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#f59e0b" />
                            <stop offset="100%" stopColor="#d97706" />
                        </linearGradient>
                        <linearGradient id="dangerGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#ef4444" />
                            <stop offset="100%" stopColor="#dc2626" />
                        </linearGradient>
                    </defs>

                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        stroke="#333333"
                        strokeWidth="8"
                        fill="none"
                    />

                    <circle
                        ref={circleRef}
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        stroke={`url(#${getGradientId()})`}
                        strokeWidth="8"
                        fill="none"
                        strokeDasharray={circumference}
                        strokeDashoffset={reducedMotion ? circumference - (percentage / 100) * circumference : circumference}
                        strokeLinecap="round"
                        style={{ filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1))' }}
                    />
                </svg>

                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="text-4xl font-display font-bold text-white">
                        {animatedPercentage}%
                    </div>
                    <div className="text-xs text-gray-400 mt-1">{getStatusText()}</div>
                </div>

                <div
                    className="absolute inset-0 rounded-full opacity-20 blur-xl animate-glow"
                    style={{ backgroundColor: getColor() }}
                    aria-hidden="true"
                />
            </div>

            <div className="mt-6 text-center">
                <h4 className="font-display font-semibold text-white">
                    {title}
                </h4>
                {subtitle && (
                    <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
                )}
            </div>
        </div>
    );
}
